// ---------------------------------------------------------------------------
// 決算書5帳票 完全レイアウト仕様 (Excel テンプレート踏襲)
//   抽出元: 合計残高試算表・科目別前年同月対比表（会社合計）・長期借入金内訳表.xlsx
//   抽出方法: openpyxl で 5テンプレシート(BS / PL / 前期PL / 科目別前年同月対比表 / 補助科目内訳表)
//             の全行(数式・スタイル・列幅・結合)を解析。
//
//   重要(会社独自仕様):
//   - 本表の小計群(現金計=8件 等)は freee の決算書表示名グループとは別の「会社独自グルーピング」。
//     よって freee の group モードに依存せず、本 layout spec が群定義(勘定科目→群・順序)を保持する。
//   - 明細行(detail)の freeeAccountName = Excel テンプレ A列に固定列挙された勘定科目名
//     (= raw シートの FILTER(科目名一致) キー)。後続 Build はこの名前で freee 全勘定科目をマッチさせる。
//   - 小計/合計(subtotal/grandtotal)の subtotalOf = Excel の =SUM(範囲) を解析し対象「行ラベル」へ解決済。
//     (実値は持たず構造のみ。Build 側が detail 実値を合算して再計算する。)
//   - 構成比分母: BS=「負債・純資産合計」(grandtotal) / PL=「純売上高」。
//
//   ロバスト化(新規/廃止勘定科目の自動追従)については本ファイル末尾 ROBUSTNESS_POLICY 参照。
//   実値ゼロ埋め・推測補完は一切しない(空応答は meta.empty / meta.unmapped で明示)。
// ---------------------------------------------------------------------------

// =============================================================================
// 行・帳票の型定義
// =============================================================================

export type LayoutRowKind =
  | 'detail' // 勘定科目明細(freeeAccountName で raw を FILTER)
  | 'subtotal' // 群小計(=SUM(範囲))
  | 'grandtotal' // 最終合計(資産合計/負債・純資産合計/当期純利益 等)
  | 'crossref' // 他帳票参照(BS 当期純損益金額 = PL 当期純利益 等)
  | 'blank'; // 群間の空行(レイアウト保持用)

/** 1帳票の上から順の 1行 */
export interface LayoutRow {
  /** Excel テンプレ上の行番号(トレーサビリティ用。blank も含め元行を保持) */
  excelRow: number;
  kind: LayoutRowKind;
  /** 表示科目名(本表=Excel A列の固定ラベル。空行は '') */
  label: string;
  /**
   * detail のとき: raw(freee account_item)とのマッチキー = Excel A列ラベル。
   * 通常 label と同一(PL は freee 勘定科目名を直接ラベルにしている)。
   */
  freeeAccountName?: string;
  /**
   * subtotal / grandtotal のとき: 合算対象の行ラベル配列(=SUM 範囲を解決済)。
   * これらラベルを同帳票内で名前解決して合算する。
   */
  subtotalOf?: string[];
  /**
   * crossref のとき: 参照先(他帳票の行ラベル等)。例: BS当期純損益金額 → PL当期純利益。
   */
  crossRef?: { sheet: 'pl' | 'pl_prev'; label: string };
  /**
   * freee の決算書表示名(group モードの集計値)で値を確定する小計/明細行。
   * 期末棚卸控除を含む「売上原価」や、leaf が存在しないカテゴリー「特別損失」等、
   * freee が特別に集計する値はテンプレ再計算でなく freee 集計値を採用する(科目増減に自動追従)。
   * この freeeCategory に属する leaf は auto-insert/unmapped から除外され二重計上を防ぐ。
   */
  freeeCategory?: string;
  /** インデント段数(0=トップ。群明細は親小計より浅い表示用) */
  indent: number;
  /** 行背景 fill。小計/合計=#D9D9D9。明細=なし */
  fill?: string;
}

/** 帳票の列定義 1要素 */
export interface LayoutColumn {
  key: string;
  label: string;
  fmt: 'currency' | 'ratio' | 'text';
  /** 列幅(Excel 文字単位) */
  width: number;
  /** ヘッダの上位グルーピング(科目別対比表の発生/残高など。なければ undefined) */
  group?: string;
  /** 値の意味(Build がデータ算出に使う): freee 列 or 算出式 */
  source?:
    | 'opening' // 前月残高 = opening_balance
    | 'debit' // 借方 = debit_amount
    | 'credit' // 貸方 = credit_amount
    | 'closing' // 残高 = closing_balance
    | 'compositionRatio' // 構成比 = 残高 / 分母 * 100
    | 'genHatsu' // 当月発生 = closing - opening
    | 'genHatsuRatio' // 発生構成比 = 当月発生 / 基準発生 * 100
    | 'genHatsuPrev' // 前期当月発生
    | 'genHatsuPrevRatio'
    | 'genHatsuDiff' // 発生増減額 = 当期発生 - 前期発生
    | 'genHatsuYoy' // 発生増減率 = 当期発生 / 前期発生 * 100
    | 'closingPrev' // 前期残高
    | 'closingRatio' // 当期残高構成比
    | 'closingPrevRatio'
    | 'closingDiff' // 残高増減額
    | 'closingYoy'; // 残高増減率
}

/** 帳票全体のレイアウト仕様 */
export interface ReportLayout {
  kind: 'bs' | 'pl' | 'pl_prev' | 'yoy' | 'sub_breakdown';
  /** Excel タイトル(A1。中央/22pt) */
  title: string;
  /** 印刷向き */
  landscape: boolean;
  /** 用紙(A4) */
  paper: 'A4';
  /** ヘッダ注記 */
  notes: {
    taxNote: string; // 【税抜】等
    adjustNote: string; // 【整理仕訳の集計条件】...
    sectionNote?: string; // 【部門】本部(補助科目内訳表)
    unitNote: string; // (単位：円)
  };
  columns: LayoutColumn[];
  rows: LayoutRow[];
  /** 構成比の分母となる行ラベル(BS=負債・純資産合計 / PL=純売上高)。なければ undefined */
  ratioDenominatorLabel?: string;
}

// =============================================================================
// スタイル定数 (Excel 実測)
// =============================================================================

export const STYLE = {
  font: 'MS Mincho', // 明朝
  fontSizeBody: 12,
  fontSizeTitle: 22,
  fontSizePrintedAt: 10,
  rowHeight: 15, // 本文行高(pt)
  titleRowHeight: 37.5,
  border: {
    style: 'thin' as const, // 細線
    sides: 'TBLR' as const, // 全辺
  },
  fill: {
    header: 'CCCCCC', // ヘッダ行(科目名/前月残高/借方/貸方/残高/構成比)背景・中央
    subtotal: 'D9D9D9', // 小計/合計行 背景
  },
  numberFormat: {
    currency: '#,##0', // マイナスは先頭 - (BS/PL/補助)
    currencyTriangle: '#,##0;"▲ "#,##0', // 科目別対比表は負数を ▲ 表示
    ratio: '0.00', // 構成比(本表)
    ratioTriangle: '#,##0.00;"▲ "#,##0.00', // 科目別対比表の構成比/増減率
  },
  align: {
    titleCenter: 'center' as const,
    headerLabelLeft: 'left' as const, // 科目名ヘッダ
    headerColCenter: 'center' as const, // 数値列ヘッダ
    companyLeft: 'left' as const,
    printedAtRight: 'right' as const,
    taxNoteRight: 'right' as const,
    unitNoteRight: 'right' as const,
  },
  /** 群と群の間に空行を1行入れる(レイアウト踏襲) */
  blankBetweenGroups: true,
} as const;

/** 列幅(文字単位)。本表(BS/PL/前期PL/補助) */
export const COLUMN_WIDTHS_TRIAL = {
  A: 27.6, // 科目名
  // 金額4列(前月残高/借方/貸方/残高)は同種なので画面プレビューも均一幅に統一(2026-06-02)。
  // 旧 Excel 実測値は B=18.9/C=D=E=13 で前月残高だけ広かった。印刷側は PRINT_CSS の等幅%で別途均一。
  B: 16.0, // 前月残高
  C: 16.0, // 借方
  D: 16.0, // 貸方
  E: 16.0, // 残高
  F: 10.1, // 構成比
} as const;

/** 列幅(文字単位)。補助科目内訳表(構成比なし。A=26.4) */
export const COLUMN_WIDTHS_SUB = {
  A: 26.4,
  // 金額4列(前月残高/借方/貸方/残高)は同種なので画面プレビューも均一幅に統一(2026-06-02)。
  B: 16.0,
  C: 16.0,
  D: 16.0,
  E: 16.0,
} as const;

/** 列幅(文字単位)。科目別前年同月対比表(13列・A4横) */
export const COLUMN_WIDTHS_YOY = {
  A: 25.1,
  B: 15.1,
  C: 6.4,
  D: 15.1,
  E: 6.4,
  F: 15.1,
  G: 6.4,
  H: 15.1,
  I: 6.4,
  J: 15.1,
  K: 6.4,
  L: 15.1,
  M: 6.4,
} as const;

/** 会社名・会社固定値 */
export const COMPANY = {
  name: 'クマガイコーポレーション株式会社',
  companyId: 11518223,
  honbuSectionId: 3068540, // 001 本部 (補助科目内訳表の部門)
} as const;

// =============================================================================
// 本表の列定義
// =============================================================================

export const TRIAL_COLUMNS: LayoutColumn[] = [
  { key: 'label', label: '科目名', fmt: 'text', width: COLUMN_WIDTHS_TRIAL.A },
  { key: 'opening', label: '前月残高', fmt: 'currency', width: COLUMN_WIDTHS_TRIAL.B, source: 'opening' },
  { key: 'debit', label: '借方', fmt: 'currency', width: COLUMN_WIDTHS_TRIAL.C, source: 'debit' },
  { key: 'credit', label: '貸方', fmt: 'currency', width: COLUMN_WIDTHS_TRIAL.D, source: 'credit' },
  { key: 'closing', label: '残高', fmt: 'currency', width: COLUMN_WIDTHS_TRIAL.E, source: 'closing' },
  { key: 'ratio', label: '構成比', fmt: 'ratio', width: COLUMN_WIDTHS_TRIAL.F, source: 'compositionRatio' },
];

export const SUB_COLUMNS: LayoutColumn[] = [
  { key: 'label', label: '科目名', fmt: 'text', width: COLUMN_WIDTHS_SUB.A },
  { key: 'opening', label: '前月残高', fmt: 'currency', width: COLUMN_WIDTHS_SUB.B, source: 'opening' },
  { key: 'debit', label: '借方', fmt: 'currency', width: COLUMN_WIDTHS_SUB.C, source: 'debit' },
  { key: 'credit', label: '貸方', fmt: 'currency', width: COLUMN_WIDTHS_SUB.D, source: 'credit' },
  { key: 'closing', label: '残高', fmt: 'currency', width: COLUMN_WIDTHS_SUB.E, source: 'closing' },
];

/** 科目別前年同月対比表: 13列。2大グループ(発生 B6:G6 / 残高 H6:M6) × 6列(当期/構成比/前期/構成比/増減額/構成比[=増減率]) */
export const YOY_COLUMNS: LayoutColumn[] = [
  { key: 'label', label: '科目名', fmt: 'text', width: COLUMN_WIDTHS_YOY.A },
  // --- 発生(当月発生額 = closing - opening) ---
  { key: 'h_cur', label: '当期', fmt: 'currency', width: COLUMN_WIDTHS_YOY.B, group: '発生', source: 'genHatsu' },
  { key: 'h_cur_r', label: '構成比', fmt: 'ratio', width: COLUMN_WIDTHS_YOY.C, group: '発生', source: 'genHatsuRatio' },
  { key: 'h_prev', label: '前期', fmt: 'currency', width: COLUMN_WIDTHS_YOY.D, group: '発生', source: 'genHatsuPrev' },
  { key: 'h_prev_r', label: '構成比', fmt: 'ratio', width: COLUMN_WIDTHS_YOY.E, group: '発生', source: 'genHatsuPrevRatio' },
  { key: 'h_diff', label: '増減額', fmt: 'currency', width: COLUMN_WIDTHS_YOY.F, group: '発生', source: 'genHatsuDiff' },
  { key: 'h_yoy', label: '構成比', fmt: 'ratio', width: COLUMN_WIDTHS_YOY.G, group: '発生', source: 'genHatsuYoy' },
  // --- 残高(累計 = closing) ---
  { key: 'b_cur', label: '当期', fmt: 'currency', width: COLUMN_WIDTHS_YOY.H, group: '残高', source: 'closing' },
  { key: 'b_cur_r', label: '構成比', fmt: 'ratio', width: COLUMN_WIDTHS_YOY.I, group: '残高', source: 'closingRatio' },
  { key: 'b_prev', label: '前期', fmt: 'currency', width: COLUMN_WIDTHS_YOY.J, group: '残高', source: 'closingPrev' },
  { key: 'b_prev_r', label: '構成比', fmt: 'ratio', width: COLUMN_WIDTHS_YOY.K, group: '残高', source: 'closingPrevRatio' },
  { key: 'b_diff', label: '増減額', fmt: 'currency', width: COLUMN_WIDTHS_YOY.L, group: '残高', source: 'closingDiff' },
  { key: 'b_yoy', label: '構成比', fmt: 'ratio', width: COLUMN_WIDTHS_YOY.M, group: '残高', source: 'closingYoy' },
];

/** 科目別対比表のヘッダ2段構造(印刷時) */
export const YOY_HEADER_GROUPS = [
  { label: '', span: 1 }, // 科目名(A6:A7 縦結合)
  { label: '発生', span: 6 }, // B6:G6
  { label: '残高', span: 6 }, // H6:M6
] as const;

// =============================================================================
// 1. 合計残高試算表(BS)  Excel rows 7-146
//    群: 現金計 / 預金計 / 現金預金計 / 当座試算計 / 棚卸資産計 / その他流動資産計 / 流動資産計 /
//        有形固定資産計 / 無形固定資産計 / 投資その他の資産計 / 固定資産計 / 繰延資産計 / 資産合計 /
//        流動負債計 / 固定負債計 / 負債合計 / (純資産各小計) / 純資産合計 / 負債・純資産合計
//    ※ 一部ラベルは Excel 上で重複(利益準備金 r110明細 / r118小計, 資本準備金 r111小計)→ 元行を保持。
// =============================================================================

export const BS_LAYOUT: ReportLayout = {
  kind: 'bs',
  title: ' 合 計 残 高 試 算 表 ',
  landscape: false,
  paper: 'A4',
  notes: {
    taxNote: '【税抜】',
    adjustNote: '【整理仕訳の集計条件】振戻仕訳・整理仕訳を集計しない',
    unitNote: '(単位：円)',
  },
  columns: TRIAL_COLUMNS,
  ratioDenominatorLabel: '負債・純資産合計',
  rows: [
    // --- 現金計 ---
    { excelRow: 7, kind: 'detail', label: '現金', freeeAccountName: '現金', indent: 1 },
    { excelRow: 8, kind: 'detail', label: '小口現金', freeeAccountName: '小口現金', indent: 1 },
    { excelRow: 9, kind: 'detail', label: '釣銭用現金', freeeAccountName: '釣銭用現金', indent: 1 },
    { excelRow: 10, kind: 'detail', label: '現金（売上）', freeeAccountName: '現金（売上）', indent: 1 },
    { excelRow: 11, kind: 'detail', label: '本部両替金', freeeAccountName: '本部両替金', indent: 1 },
    { excelRow: 12, kind: 'detail', label: '売上現金', freeeAccountName: '売上現金', indent: 1 },
    { excelRow: 13, kind: 'detail', label: '本部入金高', freeeAccountName: '本部入金高', indent: 1 },
    { excelRow: 14, kind: 'detail', label: '入金機投入高', freeeAccountName: '入金機投入高', indent: 1 },
    {
      excelRow: 15, kind: 'subtotal', label: '現金計', fill: STYLE.fill.subtotal, indent: 0,
      subtotalOf: ['現金', '小口現金', '釣銭用現金', '現金（売上）', '本部両替金', '売上現金', '本部入金高', '入金機投入高'],
    },
    { excelRow: 16, kind: 'blank', label: '', indent: 0 },
    // --- 預金計 ---
    { excelRow: 17, kind: 'detail', label: '三井住友銀行普通預金', freeeAccountName: '三井住友銀行普通預金', indent: 1 },
    { excelRow: 18, kind: 'detail', label: '三井住友現金管理口座', freeeAccountName: '三井住友現金管理口座', indent: 1 },
    { excelRow: 19, kind: 'detail', label: '三井住友銀行新宿口', freeeAccountName: '三井住友銀行新宿口', indent: 1 },
    { excelRow: 20, kind: 'detail', label: '三井住友銀行当座預金', freeeAccountName: '三井住友銀行当座預金', indent: 1 },
    { excelRow: 21, kind: 'detail', label: '三井住友外貨預金', freeeAccountName: '三井住友外貨預金', indent: 1 },
    { excelRow: 22, kind: 'detail', label: '西武信用金庫現金管理', freeeAccountName: '西武信用金庫現金管理', indent: 1 },
    { excelRow: 23, kind: 'detail', label: '西武信用金庫当座預金', freeeAccountName: '西武信用金庫当座預金', indent: 1 },
    { excelRow: 24, kind: 'detail', label: '三菱東京UFJ渋谷普通', freeeAccountName: '三菱東京UFJ渋谷普通', indent: 1 },
    { excelRow: 25, kind: 'detail', label: '三菱東京UFJ新宿普通', freeeAccountName: '三菱東京UFJ新宿普通', indent: 1 },
    { excelRow: 26, kind: 'detail', label: '三菱東京UFJ喜乃字屋', freeeAccountName: '三菱東京UFJ喜乃字屋', indent: 1 },
    { excelRow: 27, kind: 'detail', label: '商工組合中央金庫普通', freeeAccountName: '商工組合中央金庫普通', indent: 1 },
    { excelRow: 28, kind: 'detail', label: '商工中金外国預金', freeeAccountName: '商工中金外国預金', indent: 1 },
    { excelRow: 29, kind: 'detail', label: 'みずほ銀行普通預金', freeeAccountName: 'みずほ銀行普通預金', indent: 1 },
    { excelRow: 30, kind: 'detail', label: 'りそな銀行当座預金', freeeAccountName: 'りそな銀行当座預金', indent: 1 },
    { excelRow: 31, kind: 'detail', label: '興産信金普通預金', freeeAccountName: '興産信金普通預金', indent: 1 },
    {
      excelRow: 32, kind: 'subtotal', label: '預金計', fill: STYLE.fill.subtotal, indent: 0,
      subtotalOf: ['三井住友銀行普通預金', '三井住友現金管理口座', '三井住友銀行新宿口', '三井住友銀行当座預金', '三井住友外貨預金', '西武信用金庫現金管理', '西武信用金庫当座預金', '三菱東京UFJ渋谷普通', '三菱東京UFJ新宿普通', '三菱東京UFJ喜乃字屋', '商工組合中央金庫普通', '商工中金外国預金', 'みずほ銀行普通預金', 'りそな銀行当座預金', '興産信金普通預金'],
    },
    { excelRow: 33, kind: 'blank', label: '', indent: 0 },
    // --- 現金預金計(現金計 + 預金計) ---
    { excelRow: 34, kind: 'subtotal', label: '現金預金計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['現金計', '預金計'] },
    { excelRow: 35, kind: 'blank', label: '', indent: 0 },
    // --- 当座試算計 ---
    { excelRow: 36, kind: 'detail', label: '売掛金', freeeAccountName: '売掛金', indent: 1 },
    { excelRow: 37, kind: 'detail', label: '未収金', freeeAccountName: '未収金', indent: 1 },
    { excelRow: 38, kind: 'detail', label: 'Square売掛金', freeeAccountName: 'Square売掛金', indent: 1 },
    { excelRow: 39, kind: 'subtotal', label: '当座試算計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['売掛金', '未収金', 'Square売掛金'] },
    { excelRow: 40, kind: 'blank', label: '', indent: 0 },
    // --- 棚卸資産計 ---
    { excelRow: 41, kind: 'detail', label: '商品', freeeAccountName: '商品', indent: 1 },
    { excelRow: 42, kind: 'detail', label: '材料', freeeAccountName: '材料', indent: 1 },
    { excelRow: 43, kind: 'detail', label: '包装材', freeeAccountName: '包装材', indent: 1 },
    { excelRow: 44, kind: 'detail', label: '消耗品', freeeAccountName: '消耗品', indent: 1 },
    { excelRow: 45, kind: 'subtotal', label: '棚卸資産計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['商品', '材料', '包装材', '消耗品'] },
    { excelRow: 46, kind: 'blank', label: '', indent: 0 },
    // --- その他流動資産計 ---
    { excelRow: 47, kind: 'detail', label: '未収入金', freeeAccountName: '未収入金', indent: 1 },
    { excelRow: 48, kind: 'detail', label: '前払費用', freeeAccountName: '前払費用', indent: 1 },
    { excelRow: 49, kind: 'detail', label: '立替金', freeeAccountName: '立替金', indent: 1 },
    { excelRow: 50, kind: 'detail', label: '仮払金', freeeAccountName: '仮払金', indent: 1 },
    { excelRow: 51, kind: 'detail', label: '仮払消費税', freeeAccountName: '仮払消費税', indent: 1 },
    { excelRow: 52, kind: 'detail', label: '社員仮払金', freeeAccountName: '社員仮払金', indent: 1 },
    { excelRow: 53, kind: 'detail', label: '相違金', freeeAccountName: '相違金', indent: 1 },
    { excelRow: 54, kind: 'detail', label: '未収収益', freeeAccountName: '未収収益', indent: 1 },
    { excelRow: 55, kind: 'detail', label: '決済口座', freeeAccountName: '決済口座', indent: 1 },
    {
      excelRow: 56, kind: 'subtotal', label: 'その他流動資産計', fill: STYLE.fill.subtotal, indent: 0,
      subtotalOf: ['未収入金', '前払費用', '立替金', '仮払金', '仮払消費税', '社員仮払金', '相違金', '未収収益', '決済口座'],
    },
    { excelRow: 57, kind: 'blank', label: '', indent: 0 },
    // --- 流動資産計(各群小計の合算) ---
    { excelRow: 58, kind: 'subtotal', label: '流動資産計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['その他流動資産計', '棚卸資産計', '当座試算計', '預金計', '現金計'] },
    { excelRow: 59, kind: 'blank', label: '', indent: 0 },
    // --- 有形固定資産計 ---
    { excelRow: 60, kind: 'detail', label: '建物附属設備', freeeAccountName: '建物附属設備', indent: 1 },
    { excelRow: 61, kind: 'detail', label: '構築物', freeeAccountName: '構築物', indent: 1 },
    { excelRow: 62, kind: 'detail', label: '什器備品', freeeAccountName: '什器備品', indent: 1 },
    { excelRow: 63, kind: 'detail', label: '造作', freeeAccountName: '造作', indent: 1 },
    { excelRow: 64, kind: 'detail', label: '建設仮勘定', freeeAccountName: '建設仮勘定', indent: 1 },
    { excelRow: 65, kind: 'detail', label: '減価償却累計額', freeeAccountName: '減価償却累計額', indent: 1 },
    { excelRow: 66, kind: 'subtotal', label: '有形固定資産計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['建物附属設備', '構築物', '什器備品', '造作', '建設仮勘定', '減価償却累計額'] },
    { excelRow: 67, kind: 'blank', label: '', indent: 0 },
    // --- 無形固定資産計 ---
    { excelRow: 68, kind: 'detail', label: '電話加入権', freeeAccountName: '電話加入権', indent: 1 },
    { excelRow: 69, kind: 'detail', label: 'ソフトウェア', freeeAccountName: 'ソフトウェア', indent: 1 },
    { excelRow: 70, kind: 'subtotal', label: '無形固定資産計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['電話加入権', 'ソフトウェア'] },
    { excelRow: 71, kind: 'blank', label: '', indent: 0 },
    // --- 投資その他の資産計 ---
    { excelRow: 72, kind: 'detail', label: '出資金', freeeAccountName: '出資金', indent: 1 },
    { excelRow: 73, kind: 'detail', label: '差入保証金', freeeAccountName: '差入保証金', indent: 1 },
    { excelRow: 74, kind: 'detail', label: '差入敷金', freeeAccountName: '差入敷金', indent: 1 },
    { excelRow: 75, kind: 'detail', label: '保険積立金', freeeAccountName: '保険積立金', indent: 1 },
    { excelRow: 76, kind: 'subtotal', label: '投資その他の資産計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['出資金', '差入保証金', '差入敷金', '保険積立金'] },
    { excelRow: 77, kind: 'blank', label: '', indent: 0 },
    // --- 固定資産計 ---
    { excelRow: 78, kind: 'subtotal', label: '固定資産計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['有形固定資産計', '無形固定資産計', '投資その他の資産計'] },
    { excelRow: 79, kind: 'blank', label: '', indent: 0 },
    // --- 繰延資産計 ---
    { excelRow: 80, kind: 'detail', label: '開発費', freeeAccountName: '開発費', indent: 1 },
    { excelRow: 81, kind: 'subtotal', label: '繰延資産計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['開発費'] },
    { excelRow: 82, kind: 'blank', label: '', indent: 0 },
    // --- 資産合計 ---
    { excelRow: 83, kind: 'grandtotal', label: '資産合計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['流動資産計', '固定資産計', '繰延資産計'] },
    { excelRow: 84, kind: 'blank', label: '', indent: 0 },
    // --- 流動負債計 (未決済項目は流動負債計の SUM 範囲外。元 Excel どおり明細としては残すが小計対象外) ---
    { excelRow: 85, kind: 'detail', label: '未決済項目', freeeAccountName: '未決済項目', indent: 1 },
    { excelRow: 86, kind: 'detail', label: '買掛金', freeeAccountName: '買掛金', indent: 1 },
    { excelRow: 87, kind: 'detail', label: '未払金', freeeAccountName: '未払金', indent: 1 },
    { excelRow: 88, kind: 'detail', label: '三井住友VISAカード', freeeAccountName: '三井住友VISAカード', indent: 1 },
    { excelRow: 89, kind: 'detail', label: '未払費用（Amazon）', freeeAccountName: '未払費用（Amazon）', indent: 1 },
    { excelRow: 90, kind: 'detail', label: '役員資金', freeeAccountName: '役員資金', indent: 1 },
    { excelRow: 91, kind: 'detail', label: '未払費用', freeeAccountName: '未払費用', indent: 1 },
    { excelRow: 92, kind: 'detail', label: '前受金', freeeAccountName: '前受金', indent: 1 },
    { excelRow: 93, kind: 'detail', label: '仮受金', freeeAccountName: '仮受金', indent: 1 },
    { excelRow: 94, kind: 'detail', label: '仮受消費税', freeeAccountName: '仮受消費税', indent: 1 },
    { excelRow: 95, kind: 'detail', label: '社員預り金', freeeAccountName: '社員預り金', indent: 1 },
    { excelRow: 96, kind: 'detail', label: '源泉課税預り金', freeeAccountName: '源泉課税預り金', indent: 1 },
    { excelRow: 97, kind: 'detail', label: '住民税預り金', freeeAccountName: '住民税預り金', indent: 1 },
    { excelRow: 98, kind: 'detail', label: '未払法人税等', freeeAccountName: '未払法人税等', indent: 1 },
    {
      excelRow: 99, kind: 'subtotal', label: '流動負債計', fill: STYLE.fill.subtotal, indent: 0,
      // 元 Excel の =SUM(B86:B98)。未決済項目(r85)は SUM 範囲外。
      subtotalOf: ['買掛金', '未払金', '三井住友VISAカード', '未払費用（Amazon）', '役員資金', '未払費用', '前受金', '仮受金', '仮受消費税', '社員預り金', '源泉課税預り金', '住民税預り金', '未払法人税等'],
    },
    { excelRow: 100, kind: 'blank', label: '', indent: 0 },
    // --- 固定負債計 ---
    { excelRow: 101, kind: 'detail', label: '長期借入金', freeeAccountName: '長期借入金', indent: 1 },
    { excelRow: 102, kind: 'subtotal', label: '固定負債計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['長期借入金'] },
    { excelRow: 103, kind: 'blank', label: '', indent: 0 },
    // --- 負債合計 ---
    { excelRow: 104, kind: 'grandtotal', label: '負債合計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['流動負債計', '固定負債計'] },
    { excelRow: 105, kind: 'blank', label: '', indent: 0 },
    // --- 純資産の部 (Excel 上は小計行も明細も #D9D9D9。0固定の項目も将来値が入りうるため detail 保持) ---
    { excelRow: 106, kind: 'detail', label: '資本金', freeeAccountName: '資本金', indent: 0, fill: STYLE.fill.subtotal },
    { excelRow: 107, kind: 'blank', label: '', indent: 0 },
    { excelRow: 108, kind: 'detail', label: '新株式申込証拠金', freeeAccountName: '新株式申込証拠金', indent: 1, fill: STYLE.fill.subtotal },
    { excelRow: 109, kind: 'blank', label: '', indent: 0 },
    { excelRow: 110, kind: 'detail', label: '利益準備金', freeeAccountName: '利益準備金', indent: 1, fill: STYLE.fill.subtotal },
    // r111 資本準備金(小計) = SUM(利益準備金 r110)。Excel 上は群ラベルが付け替わっており重複ラベルが存在。
    { excelRow: 111, kind: 'subtotal', label: '資本準備金', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['利益準備金'] },
    { excelRow: 112, kind: 'blank', label: '', indent: 0 },
    { excelRow: 113, kind: 'detail', label: 'その他資本剰余金計', freeeAccountName: 'その他資本剰余金', indent: 1, fill: STYLE.fill.subtotal },
    { excelRow: 114, kind: 'blank', label: '', indent: 0 },
    { excelRow: 115, kind: 'subtotal', label: '資本剰余金計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['新株式申込証拠金', '資本準備金', 'その他資本剰余金計'] },
    { excelRow: 116, kind: 'blank', label: '', indent: 0 },
    { excelRow: 117, kind: 'detail', label: '別途積立金', freeeAccountName: '別途積立金', indent: 1, fill: STYLE.fill.subtotal },
    // r118 利益準備金(小計) = SUM(別途積立金 r117)。※ r110 利益準備金(明細)とラベル重複。Build は excelRow で区別。
    { excelRow: 118, kind: 'subtotal', label: '利益準備金', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['別途積立金'] },
    { excelRow: 119, kind: 'blank', label: '', indent: 0 },
    { excelRow: 120, kind: 'detail', label: '繰越利益剰余金', freeeAccountName: '繰越利益剰余金', indent: 1, fill: STYLE.fill.subtotal },
    { excelRow: 121, kind: 'blank', label: '', indent: 0 },
    // r122 その他利益剰余金計 = B120 + B118 (繰越利益剰余金 + 利益準備金小計)
    { excelRow: 122, kind: 'subtotal', label: 'その他利益剰余金計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['繰越利益剰余金', '利益準備金'] },
    { excelRow: 123, kind: 'blank', label: '', indent: 0 },
    // r124 利益剰余金計 = SUM(B111, B122) = 資本準備金小計 + その他利益剰余金計
    { excelRow: 124, kind: 'subtotal', label: '利益剰余金計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['資本準備金', 'その他利益剰余金計'] },
    { excelRow: 125, kind: 'blank', label: '', indent: 0 },
    { excelRow: 126, kind: 'detail', label: '自己株式', freeeAccountName: '自己株式', indent: 1, fill: STYLE.fill.subtotal },
    { excelRow: 127, kind: 'blank', label: '', indent: 0 },
    { excelRow: 128, kind: 'detail', label: '自己株式申込証拠金', freeeAccountName: '自己株式申込証拠金', indent: 1, fill: STYLE.fill.subtotal },
    { excelRow: 129, kind: 'blank', label: '', indent: 0 },
    // r130 当期純損益金額 = PL 当期純利益 のクロス参照(=SUM('合計残高試算表（PL）'!B83))
    { excelRow: 130, kind: 'crossref', label: '当期純損益金額', indent: 0, fill: STYLE.fill.subtotal, crossRef: { sheet: 'pl', label: '当期純利益' } },
    { excelRow: 131, kind: 'blank', label: '', indent: 0 },
    // r132 株主資本計 = SUM(B106資本金, B115資本剰余金計, B118利益準備金小計, B120繰越利益剰余金, B130当期純損益金額)
    { excelRow: 132, kind: 'subtotal', label: '株主資本計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['資本金', '資本剰余金計', '利益準備金', '繰越利益剰余金', '当期純損益金額'] },
    { excelRow: 133, kind: 'blank', label: '', indent: 0 },
    { excelRow: 134, kind: 'detail', label: '有価証券評価差額金', freeeAccountName: '有価証券評価差額金', indent: 1, fill: STYLE.fill.subtotal },
    { excelRow: 135, kind: 'blank', label: '', indent: 0 },
    { excelRow: 136, kind: 'detail', label: '繰延ヘッジ損益', freeeAccountName: '繰延ヘッジ損益', indent: 1, fill: STYLE.fill.subtotal },
    { excelRow: 137, kind: 'blank', label: '', indent: 0 },
    { excelRow: 138, kind: 'detail', label: '土地再評価差額金', freeeAccountName: '土地再評価差額金', indent: 1, fill: STYLE.fill.subtotal },
    { excelRow: 139, kind: 'blank', label: '', indent: 0 },
    { excelRow: 140, kind: 'subtotal', label: '評価・換算差額等計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['有価証券評価差額金', '繰延ヘッジ損益', '土地再評価差額金'] },
    { excelRow: 141, kind: 'blank', label: '', indent: 0 },
    { excelRow: 142, kind: 'detail', label: '新株予約権', freeeAccountName: '新株予約権', indent: 1, fill: STYLE.fill.subtotal },
    { excelRow: 143, kind: 'blank', label: '', indent: 0 },
    // r144 純資産合計 = SUM(B132株主資本計, B140評価換算差額等計, B142新株予約権)
    { excelRow: 144, kind: 'grandtotal', label: '純資産合計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['株主資本計', '評価・換算差額等計', '新株予約権'] },
    { excelRow: 145, kind: 'blank', label: '', indent: 0 },
    // r146 負債・純資産合計 = SUM(B104負債合計, B144純資産合計)  ← 構成比の分母
    { excelRow: 146, kind: 'grandtotal', label: '負債・純資産合計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['負債合計', '純資産合計'] },
  ],
};

// =============================================================================
// 2. 合計残高試算表(PL) Excel rows 7-83
//    PL 明細の A列ラベル = freee 勘定科目名(地代家賃/荷造運賃/交際費 等)を直接表示。
//    小計: 純売上高 / 売上原価 / 売上総利益(=純売上高-売上原価) / 人件費 / 販売経費 /
//          販売費及び一般管理費(=人件費+販売経費) / 営業利益(=売上総利益-販管費) /
//          営業外収益 / 営業外費用 / 経常利益(=営業利益+営業外収益) /
//          特別利益 / 税引前当期純利益(=経常利益+特別利益) / 当期純利益(=税引前-法人税等)
//    ※ 営業利益・経常利益・税引前・当期純利益は SUM ではなく差引式 → kind=subtotal + subtotalOf に
//      正負の意味を込めず「構成要素ラベル」を列挙(Build は PL_DERIVED_FORMULAS で符号付き算出する)。
// =============================================================================

export const PL_LAYOUT: ReportLayout = {
  kind: 'pl',
  title: ' 合 計 残 高 試 算 表 ',
  landscape: false,
  paper: 'A4',
  notes: {
    taxNote: '【税抜】',
    adjustNote: '【整理仕訳の集計条件】振戻仕訳・整理仕訳を集計しない',
    unitNote: '(単位：円)',
  },
  columns: TRIAL_COLUMNS,
  ratioDenominatorLabel: '純売上高',
  rows: [
    // --- 純売上高 ---
    { excelRow: 7, kind: 'detail', label: '売上高', freeeAccountName: '売上高', indent: 1 },
    { excelRow: 8, kind: 'detail', label: '煙草売上高', freeeAccountName: '煙草売上高', indent: 1 },
    { excelRow: 9, kind: 'detail', label: 'ワイン事業売上高', freeeAccountName: 'ワイン事業売上高', indent: 1 },
    { excelRow: 10, kind: 'detail', label: '社員割引', freeeAccountName: '社員割引', indent: 1 },
    { excelRow: 11, kind: 'subtotal', label: '純売上高', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['売上高', '煙草売上高', 'ワイン事業売上高', '社員割引'] },
    { excelRow: 12, kind: 'blank', label: '', indent: 0 },
    // --- 売上原価 ---
    { excelRow: 13, kind: 'detail', label: '期首商品棚卸高', freeeAccountName: '期首商品棚卸高', indent: 1 },
    { excelRow: 14, kind: 'detail', label: '包装材期首棚卸高', freeeAccountName: '包装材期首棚卸高', indent: 1 },
    { excelRow: 15, kind: 'detail', label: '仕入高', freeeAccountName: '仕入高', indent: 1 },
    { excelRow: 16, kind: 'detail', label: 'ワイン仕入', freeeAccountName: 'ワイン仕入', indent: 1 },
    { excelRow: 17, kind: 'detail', label: '包装材仕入高', freeeAccountName: '包装材仕入高', indent: 1 },
    { excelRow: 18, kind: 'detail', label: '期末商品棚卸高', freeeAccountName: '期末商品棚卸高', indent: 1 },
    { excelRow: 19, kind: 'detail', label: '包装材期末棚卸高', freeeAccountName: '包装材期末棚卸高', indent: 1 },
    // 売上原価 = 期首棚卸 + 仕入 - 期末棚卸。freee の「売上原価」カテゴリー集計値を採用
    //   (期末棚卸控除込み・新規仕入科目に自動追従)。group 取得失敗時は PL_DERIVED_FORMULAS の
    //   符号付き式(期首+仕入-期末)で fallback する。
    { excelRow: 20, kind: 'subtotal', label: '売上原価', fill: STYLE.fill.subtotal, indent: 0, freeeCategory: '売上原価', subtotalOf: ['期首商品棚卸高', '包装材期首棚卸高', '仕入高', 'ワイン仕入', '包装材仕入高'] },
    { excelRow: 21, kind: 'blank', label: '', indent: 0 },
    // --- 売上総利益 = 純売上高 - 売上原価 ---
    { excelRow: 22, kind: 'subtotal', label: '売上総利益', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['純売上高', '売上原価'] },
    { excelRow: 23, kind: 'blank', label: '', indent: 0 },
    // --- 人件費 ---
    { excelRow: 24, kind: 'detail', label: '役員報酬', freeeAccountName: '役員報酬', indent: 1 },
    { excelRow: 25, kind: 'detail', label: '給与手当', freeeAccountName: '給与手当', indent: 1 },
    { excelRow: 26, kind: 'detail', label: '雑給', freeeAccountName: '雑給', indent: 1 },
    { excelRow: 27, kind: 'detail', label: '賞与', freeeAccountName: '賞与', indent: 1 },
    { excelRow: 28, kind: 'detail', label: '法定福利費', freeeAccountName: '法定福利費', indent: 1 },
    { excelRow: 29, kind: 'detail', label: '福利厚生費', freeeAccountName: '福利厚生費', indent: 1 },
    { excelRow: 30, kind: 'detail', label: '通勤費', freeeAccountName: '通勤費', indent: 1 },
    { excelRow: 31, kind: 'detail', label: '研修費', freeeAccountName: '研修費', indent: 1 },
    { excelRow: 32, kind: 'detail', label: '募集費', freeeAccountName: '募集費', indent: 1 },
    { excelRow: 33, kind: 'subtotal', label: '人件費', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['役員報酬', '給与手当', '雑給', '賞与', '法定福利費', '福利厚生費', '通勤費', '研修費', '募集費'] },
    { excelRow: 34, kind: 'blank', label: '', indent: 0 },
    // --- 販売経費 ---
    { excelRow: 35, kind: 'detail', label: '消耗品費', freeeAccountName: '消耗品費', indent: 1 },
    { excelRow: 36, kind: 'detail', label: '事務用品費', freeeAccountName: '事務用品費', indent: 1 },
    { excelRow: 37, kind: 'detail', label: '地代家賃', freeeAccountName: '地代家賃', indent: 1 },
    { excelRow: 38, kind: 'detail', label: '賃借料', freeeAccountName: '賃借料', indent: 1 },
    { excelRow: 39, kind: 'detail', label: '保険料', freeeAccountName: '保険料', indent: 1 },
    { excelRow: 40, kind: 'detail', label: '修繕費', freeeAccountName: '修繕費', indent: 1 },
    { excelRow: 41, kind: 'detail', label: '出張費', freeeAccountName: '出張費', indent: 1 },
    { excelRow: 42, kind: 'detail', label: '租税公課', freeeAccountName: '租税公課', indent: 1 },
    { excelRow: 43, kind: 'detail', label: '減価償却費', freeeAccountName: '減価償却費', indent: 1 },
    { excelRow: 44, kind: 'detail', label: '旅費交通費', freeeAccountName: '旅費交通費', indent: 1 },
    { excelRow: 45, kind: 'detail', label: '通信費', freeeAccountName: '通信費', indent: 1 },
    { excelRow: 46, kind: 'detail', label: '水道光熱費', freeeAccountName: '水道光熱費', indent: 1 },
    { excelRow: 47, kind: 'detail', label: '支払手数料', freeeAccountName: '支払手数料', indent: 1 },
    { excelRow: 48, kind: 'detail', label: '荷造運賃', freeeAccountName: '荷造運賃', indent: 1 },
    { excelRow: 49, kind: 'detail', label: '広告宣伝費', freeeAccountName: '広告宣伝費', indent: 1 },
    { excelRow: 50, kind: 'detail', label: '交際費', freeeAccountName: '交際費', indent: 1 },
    { excelRow: 51, kind: 'detail', label: '会議費', freeeAccountName: '会議費', indent: 1 },
    { excelRow: 52, kind: 'detail', label: '燃料費', freeeAccountName: '燃料費', indent: 1 },
    { excelRow: 53, kind: 'detail', label: '諸会費', freeeAccountName: '諸会費', indent: 1 },
    { excelRow: 54, kind: 'detail', label: '新聞図書費', freeeAccountName: '新聞図書費', indent: 1 },
    { excelRow: 55, kind: 'detail', label: '顧問料', freeeAccountName: '顧問料', indent: 1 },
    { excelRow: 56, kind: 'detail', label: '雑費', freeeAccountName: '雑費', indent: 1 },
    {
      excelRow: 57, kind: 'subtotal', label: '販売経費', fill: STYLE.fill.subtotal, indent: 0,
      subtotalOf: ['消耗品費', '事務用品費', '地代家賃', '賃借料', '保険料', '修繕費', '出張費', '租税公課', '減価償却費', '旅費交通費', '通信費', '水道光熱費', '支払手数料', '荷造運賃', '広告宣伝費', '交際費', '会議費', '燃料費', '諸会費', '新聞図書費', '顧問料', '雑費'],
    },
    { excelRow: 58, kind: 'blank', label: '', indent: 0 },
    // --- 販売費及び一般管理費 = 人件費 + 販売経費 ---
    { excelRow: 59, kind: 'subtotal', label: '販売費及び一般管理費', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['人件費', '販売経費'] },
    { excelRow: 60, kind: 'blank', label: '', indent: 0 },
    // --- 営業利益 = 売上総利益 - 販売費及び一般管理費 ---
    { excelRow: 61, kind: 'subtotal', label: '営業利益', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['売上総利益', '販売費及び一般管理費'] },
    { excelRow: 62, kind: 'blank', label: '', indent: 0 },
    // --- 営業外収益 ---
    { excelRow: 63, kind: 'detail', label: '受取利息', freeeAccountName: '受取利息', indent: 1 },
    { excelRow: 64, kind: 'detail', label: '受取配当金', freeeAccountName: '受取配当金', indent: 1 },
    { excelRow: 65, kind: 'detail', label: '雑収入', freeeAccountName: '雑収入', indent: 1 },
    { excelRow: 66, kind: 'subtotal', label: '営業外収益', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['受取利息', '受取配当金', '雑収入'] },
    { excelRow: 67, kind: 'blank', label: '', indent: 0 },
    // --- 営業外費用 ---
    { excelRow: 68, kind: 'detail', label: '支払利息', freeeAccountName: '支払利息', indent: 1 },
    { excelRow: 69, kind: 'detail', label: '雑損失', freeeAccountName: '雑損失', indent: 1 },
    { excelRow: 70, kind: 'subtotal', label: '営業外費用', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['支払利息', '雑損失'] },
    { excelRow: 71, kind: 'blank', label: '', indent: 0 },
    // --- 経常利益 = 営業利益 + 営業外収益 - 営業外費用 (Excel は =SUM(B61,B66) だが営業外費用控除が運用上含意) ---
    { excelRow: 72, kind: 'subtotal', label: '経常利益', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['営業利益', '営業外収益'] },
    { excelRow: 73, kind: 'blank', label: '', indent: 0 },
    // --- 特別利益 ---
    { excelRow: 74, kind: 'detail', label: '債務免除益', freeeAccountName: '債務免除益', indent: 1 },
    { excelRow: 75, kind: 'detail', label: '解約金戻入', freeeAccountName: '解約金戻入', indent: 1 },
    { excelRow: 76, kind: 'subtotal', label: '特別利益', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['債務免除益', '解約金戻入'] },
    { excelRow: 77, kind: 'blank', label: '', indent: 0 },
    // --- 特別損失(明細1行のみ。Excel は #D9D9D9 の単独行) ---
    //   freee に「特別損失」leaf は無く、実体は 差入敷金償却金 等の category 配下科目。
    //   freee の「特別損失」カテゴリー集計値を採用(新規 特別損失科目に自動追従)。
    { excelRow: 78, kind: 'detail', label: '特別損失', freeeCategory: '特別損失', indent: 0, fill: STYLE.fill.subtotal },
    { excelRow: 79, kind: 'blank', label: '', indent: 0 },
    // --- 税引前当期純利益 = 経常利益 + 特別利益 - 特別損失 ---
    { excelRow: 80, kind: 'subtotal', label: '税引前当期純利益', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['経常利益', '特別利益'] },
    { excelRow: 81, kind: 'blank', label: '', indent: 0 },
    // --- 法人税等 ---
    { excelRow: 82, kind: 'detail', label: '法人税等', freeeAccountName: '法人税等', indent: 1 },
    // --- 当期純利益 = 税引前当期純利益 - 法人税等 ---
    { excelRow: 83, kind: 'grandtotal', label: '当期純利益', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['税引前当期純利益', '法人税等'] },
  ],
};

// =============================================================================
// 3. 合計残高試算表(前期PL) Excel rows 7-82
//    PL と同構造。差分(前期テンプレ実測):
//    - r16 のラベルが「ワイン仕入高」(当期PLは「ワイン仕入」)
//    - 特別利益が r74 債務免除益 のみ(解約金戻入なし)
//    - 行番号が当期より1行ずつ前(特別利益が1件少ないため税引前=r79, 当期純利益=r82)
// =============================================================================

export const PL_PREV_LAYOUT: ReportLayout = {
  kind: 'pl_prev',
  title: ' 合 計 残 高 試 算 表 ',
  landscape: false,
  paper: 'A4',
  notes: {
    taxNote: '【税抜】',
    adjustNote: '【整理仕訳の集計条件】振戻仕訳・整理仕訳を集計しない',
    unitNote: '(単位：円)',
  },
  columns: TRIAL_COLUMNS,
  ratioDenominatorLabel: '純売上高',
  rows: [
    { excelRow: 7, kind: 'detail', label: '売上高', freeeAccountName: '売上高', indent: 1 },
    { excelRow: 8, kind: 'detail', label: '煙草売上高', freeeAccountName: '煙草売上高', indent: 1 },
    { excelRow: 9, kind: 'detail', label: 'ワイン事業売上高', freeeAccountName: 'ワイン事業売上高', indent: 1 },
    { excelRow: 10, kind: 'detail', label: '社員割引', freeeAccountName: '社員割引', indent: 1 },
    { excelRow: 11, kind: 'subtotal', label: '純売上高', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['売上高', '煙草売上高', 'ワイン事業売上高', '社員割引'] },
    { excelRow: 12, kind: 'blank', label: '', indent: 0 },
    { excelRow: 13, kind: 'detail', label: '期首商品棚卸高', freeeAccountName: '期首商品棚卸高', indent: 1 },
    { excelRow: 14, kind: 'detail', label: '包装材期首棚卸高', freeeAccountName: '包装材期首棚卸高', indent: 1 },
    { excelRow: 15, kind: 'detail', label: '仕入高', freeeAccountName: '仕入高', indent: 1 },
    { excelRow: 16, kind: 'detail', label: 'ワイン仕入高', freeeAccountName: 'ワイン仕入高', indent: 1 },
    { excelRow: 17, kind: 'detail', label: '包装材仕入高', freeeAccountName: '包装材仕入高', indent: 1 },
    { excelRow: 18, kind: 'detail', label: '期末商品棚卸高', freeeAccountName: '期末商品棚卸高', indent: 1 },
    { excelRow: 19, kind: 'detail', label: '包装材期末棚卸高', freeeAccountName: '包装材期末棚卸高', indent: 1 },
    { excelRow: 20, kind: 'subtotal', label: '売上原価', fill: STYLE.fill.subtotal, indent: 0, freeeCategory: '売上原価', subtotalOf: ['期首商品棚卸高', '包装材期首棚卸高', '仕入高', 'ワイン仕入高', '包装材仕入高'] },
    { excelRow: 21, kind: 'blank', label: '', indent: 0 },
    { excelRow: 22, kind: 'subtotal', label: '売上総利益', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['純売上高', '売上原価'] },
    { excelRow: 23, kind: 'blank', label: '', indent: 0 },
    { excelRow: 24, kind: 'detail', label: '役員報酬', freeeAccountName: '役員報酬', indent: 1 },
    { excelRow: 25, kind: 'detail', label: '給与手当', freeeAccountName: '給与手当', indent: 1 },
    { excelRow: 26, kind: 'detail', label: '雑給', freeeAccountName: '雑給', indent: 1 },
    { excelRow: 27, kind: 'detail', label: '賞与', freeeAccountName: '賞与', indent: 1 },
    { excelRow: 28, kind: 'detail', label: '法定福利費', freeeAccountName: '法定福利費', indent: 1 },
    { excelRow: 29, kind: 'detail', label: '福利厚生費', freeeAccountName: '福利厚生費', indent: 1 },
    { excelRow: 30, kind: 'detail', label: '通勤費', freeeAccountName: '通勤費', indent: 1 },
    { excelRow: 31, kind: 'detail', label: '研修費', freeeAccountName: '研修費', indent: 1 },
    { excelRow: 32, kind: 'detail', label: '募集費', freeeAccountName: '募集費', indent: 1 },
    { excelRow: 33, kind: 'subtotal', label: '人件費', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['役員報酬', '給与手当', '雑給', '賞与', '法定福利費', '福利厚生費', '通勤費', '研修費', '募集費'] },
    { excelRow: 34, kind: 'blank', label: '', indent: 0 },
    { excelRow: 35, kind: 'detail', label: '消耗品費', freeeAccountName: '消耗品費', indent: 1 },
    { excelRow: 36, kind: 'detail', label: '事務用品費', freeeAccountName: '事務用品費', indent: 1 },
    { excelRow: 37, kind: 'detail', label: '地代家賃', freeeAccountName: '地代家賃', indent: 1 },
    { excelRow: 38, kind: 'detail', label: '賃借料', freeeAccountName: '賃借料', indent: 1 },
    { excelRow: 39, kind: 'detail', label: '保険料', freeeAccountName: '保険料', indent: 1 },
    { excelRow: 40, kind: 'detail', label: '修繕費', freeeAccountName: '修繕費', indent: 1 },
    { excelRow: 41, kind: 'detail', label: '出張費', freeeAccountName: '出張費', indent: 1 },
    { excelRow: 42, kind: 'detail', label: '租税公課', freeeAccountName: '租税公課', indent: 1 },
    { excelRow: 43, kind: 'detail', label: '減価償却費', freeeAccountName: '減価償却費', indent: 1 },
    { excelRow: 44, kind: 'detail', label: '旅費交通費', freeeAccountName: '旅費交通費', indent: 1 },
    { excelRow: 45, kind: 'detail', label: '通信費', freeeAccountName: '通信費', indent: 1 },
    { excelRow: 46, kind: 'detail', label: '水道光熱費', freeeAccountName: '水道光熱費', indent: 1 },
    { excelRow: 47, kind: 'detail', label: '支払手数料', freeeAccountName: '支払手数料', indent: 1 },
    { excelRow: 48, kind: 'detail', label: '荷造運賃', freeeAccountName: '荷造運賃', indent: 1 },
    { excelRow: 49, kind: 'detail', label: '広告宣伝費', freeeAccountName: '広告宣伝費', indent: 1 },
    { excelRow: 50, kind: 'detail', label: '交際費', freeeAccountName: '交際費', indent: 1 },
    { excelRow: 51, kind: 'detail', label: '会議費', freeeAccountName: '会議費', indent: 1 },
    { excelRow: 52, kind: 'detail', label: '燃料費', freeeAccountName: '燃料費', indent: 1 },
    { excelRow: 53, kind: 'detail', label: '諸会費', freeeAccountName: '諸会費', indent: 1 },
    { excelRow: 54, kind: 'detail', label: '新聞図書費', freeeAccountName: '新聞図書費', indent: 1 },
    { excelRow: 55, kind: 'detail', label: '顧問料', freeeAccountName: '顧問料', indent: 1 },
    { excelRow: 56, kind: 'detail', label: '雑費', freeeAccountName: '雑費', indent: 1 },
    {
      excelRow: 57, kind: 'subtotal', label: '販売経費', fill: STYLE.fill.subtotal, indent: 0,
      subtotalOf: ['消耗品費', '事務用品費', '地代家賃', '賃借料', '保険料', '修繕費', '出張費', '租税公課', '減価償却費', '旅費交通費', '通信費', '水道光熱費', '支払手数料', '荷造運賃', '広告宣伝費', '交際費', '会議費', '燃料費', '諸会費', '新聞図書費', '顧問料', '雑費'],
    },
    { excelRow: 58, kind: 'blank', label: '', indent: 0 },
    { excelRow: 59, kind: 'subtotal', label: '販売費及び一般管理費', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['人件費', '販売経費'] },
    { excelRow: 60, kind: 'blank', label: '', indent: 0 },
    { excelRow: 61, kind: 'subtotal', label: '営業利益', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['売上総利益', '販売費及び一般管理費'] },
    { excelRow: 62, kind: 'blank', label: '', indent: 0 },
    { excelRow: 63, kind: 'detail', label: '受取利息', freeeAccountName: '受取利息', indent: 1 },
    { excelRow: 64, kind: 'detail', label: '受取配当金', freeeAccountName: '受取配当金', indent: 1 },
    { excelRow: 65, kind: 'detail', label: '雑収入', freeeAccountName: '雑収入', indent: 1 },
    { excelRow: 66, kind: 'subtotal', label: '営業外収益', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['受取利息', '受取配当金', '雑収入'] },
    { excelRow: 67, kind: 'blank', label: '', indent: 0 },
    { excelRow: 68, kind: 'detail', label: '支払利息', freeeAccountName: '支払利息', indent: 1 },
    { excelRow: 69, kind: 'detail', label: '雑損失', freeeAccountName: '雑損失', indent: 1 },
    { excelRow: 70, kind: 'subtotal', label: '営業外費用', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['支払利息', '雑損失'] },
    { excelRow: 71, kind: 'blank', label: '', indent: 0 },
    { excelRow: 72, kind: 'subtotal', label: '経常利益', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['営業利益', '営業外収益'] },
    { excelRow: 73, kind: 'blank', label: '', indent: 0 },
    { excelRow: 74, kind: 'detail', label: '債務免除益', freeeAccountName: '債務免除益', indent: 1 },
    { excelRow: 75, kind: 'subtotal', label: '特別利益', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['債務免除益'] },
    { excelRow: 76, kind: 'blank', label: '', indent: 0 },
    { excelRow: 77, kind: 'detail', label: '特別損失', freeeCategory: '特別損失', indent: 0, fill: STYLE.fill.subtotal },
    { excelRow: 78, kind: 'blank', label: '', indent: 0 },
    { excelRow: 79, kind: 'subtotal', label: '税引前当期純利益', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['経常利益', '特別利益'] },
    { excelRow: 80, kind: 'blank', label: '', indent: 0 },
    { excelRow: 81, kind: 'detail', label: '法人税等', freeeAccountName: '法人税等', indent: 1 },
    { excelRow: 82, kind: 'grandtotal', label: '当期純利益', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['税引前当期純利益', '法人税等'] },
  ],
};

// =============================================================================
//  PL 派生小計の符号付き算出式 (Excel の差引式を明示)
//   subtotalOf は「構成要素ラベル」列挙(レイアウト構造)。実際の符号付き計算は本テーブルが正。
//   Build はまず純額系小計(純売上高/売上原価/人件費/販売経費/販管費/各外収益費用/特別利益)を
//   subtotalOf 合算で求め、その後この式で利益系を算出する。
// =============================================================================

export interface PlDerivedFormula {
  label: string;
  /** {term: 行ラベル, sign: +1|-1} の合算式 */
  terms: { label: string; sign: 1 | -1 }[];
}

export const PL_DERIVED_FORMULAS: PlDerivedFormula[] = [
  // 売上原価 = 期首棚卸 + 仕入 - 期末棚卸 (group 取得失敗時の fallback。通常は freeeCategory='売上原価' を採用)
  {
    label: '売上原価',
    terms: [
      { label: '期首商品棚卸高', sign: 1 }, { label: '包装材期首棚卸高', sign: 1 },
      { label: '仕入高', sign: 1 }, { label: 'ワイン仕入', sign: 1 }, { label: '包装材仕入高', sign: 1 },
      { label: '期末商品棚卸高', sign: -1 }, { label: '包装材期末棚卸高', sign: -1 },
    ],
  },
  { label: '売上総利益', terms: [{ label: '純売上高', sign: 1 }, { label: '売上原価', sign: -1 }] },
  { label: '販売費及び一般管理費', terms: [{ label: '人件費', sign: 1 }, { label: '販売経費', sign: 1 }] },
  { label: '営業利益', terms: [{ label: '売上総利益', sign: 1 }, { label: '販売費及び一般管理費', sign: -1 }] },
  // 経常利益 = 営業利益 + 営業外収益 - 営業外費用 (Excel 数式は =SUM(B61,B66) で営業外費用が
  //   別行控除運用だが、正しい P/L 定義に合わせ営業外費用を控除する)
  { label: '経常利益', terms: [{ label: '営業利益', sign: 1 }, { label: '営業外収益', sign: 1 }, { label: '営業外費用', sign: -1 }] },
  // 税引前当期純利益 = 経常利益 + 特別利益 - 特別損失
  { label: '税引前当期純利益', terms: [{ label: '経常利益', sign: 1 }, { label: '特別利益', sign: 1 }, { label: '特別損失', sign: -1 }] },
  // 当期純利益 = 税引前当期純利益 - 法人税等
  { label: '当期純利益', terms: [{ label: '税引前当期純利益', sign: 1 }, { label: '法人税等', sign: -1 }] },
];

// =============================================================================
// 4. 科目別前年同月対比表(会社合計) Excel rows 8-83
//    PL と同じ科目行構成(発生=closing-opening / 残高=closing)。当期 vs 前期(会計期-1)。
//    行は PL_LAYOUT の detail/subtotal/grandtotal を流用(構造同一)。列は YOY_COLUMNS(13列)。
//    分母: 発生構成比=純売上高の当月発生 / 残高構成比=純売上高の累計。
// =============================================================================

export const YOY_LAYOUT: ReportLayout = {
  kind: 'yoy',
  title: ' 科 目 別 対 比 表 ',
  landscape: true, // A4横
  paper: 'A4',
  notes: {
    taxNote: '【税抜・発生・残高】',
    adjustNote: '【整理仕訳の集計条件】振戻仕訳・整理仕訳を集計しない',
    unitNote: '(単位：円)',
  },
  columns: YOY_COLUMNS,
  ratioDenominatorLabel: '純売上高',
  // 行は PL と同一構造(当期PLの行ラベル順)。Build は PL_LAYOUT.rows のラベル順を流用し、
  // 各行に YOY_COLUMNS の 12 数値列を当期/前期から算出する。
  rows: PL_LAYOUT.rows,
};

// =============================================================================
// 5. 補助科目内訳表 (長期借入金・部門=本部) Excel rows 7-20
//    freee trial_bs breakdown_display_type=item & section_id=本部 の
//    account_item_name=='長期借入金' の items[] (借入先=品目別)。
//    構造: ヘッダ「長期借入金」(科目見出し) + items(借入先別) + 合計(=SUM)。
//    列は SUB_COLUMNS(構成比なし4数値列)。借入先件数は可変(13件枠は固定でなく items 数に追従)。
// =============================================================================

export const SUB_BREAKDOWN_LAYOUT: ReportLayout = {
  kind: 'sub_breakdown',
  title: ' 補 助 科 目 内 訳 表 ',
  landscape: false,
  paper: 'A4',
  notes: {
    taxNote: '【税抜】',
    adjustNote: '【整理仕訳の集計条件】振戻仕訳・整理仕訳を集計しない',
    sectionNote: '【部門】本部',
    unitNote: '(単位：円)',
  },
  columns: SUB_COLUMNS,
  // items は動的。固定行は科目見出し(長期借入金)と合計のみ。Build が items[] を間に展開する。
  rows: [
    { excelRow: 7, kind: 'detail', label: '長期借入金', freeeAccountName: '長期借入金', indent: 0 },
    // ↑↓ の間に freee items[] (借入先=品目) を kind:'detail' indent:1 で動的展開
    { excelRow: 20, kind: 'grandtotal', label: '合計', fill: STYLE.fill.subtotal, indent: 0, subtotalOf: ['長期借入金'] },
  ],
};

/** 補助科目内訳表の動的展開仕様 */
export const SUB_BREAKDOWN_DYNAMIC = {
  /** freee account_item_name で items[] を引く対象勘定科目 */
  parentAccountName: '長期借入金',
  /** items[] の各要素を detail 行に展開。label=借入先(品目)名。indent=1 */
  itemIndent: 1,
  /** 借入先名が空の品目の表示 */
  emptyItemLabel: '(未選択)',
  honbuSectionId: COMPANY.honbuSectionId,
} as const;

// =============================================================================
// PL 表示名 ↔ freee 勘定科目 マッピング (Excel「シート10」)
//   当期PLテンプレは既に freee 勘定科目名を A列に直接表示しているが、
//   会社の旧来表示名(家賃/運賃/交際接待費 等)からの引当が必要な場面のため保持。
//   B列(=freee 勘定科目名)が空の行は「独自小計ラベル」(freee 科目に対応せず PL 内で算出)。
//   C/D列は同義候補(別表記・部門指定)で参考情報。
// =============================================================================

export interface PlMappingEntry {
  /** シート10 A列: 会社表示名 */
  displayName: string;
  /** シート10 B列: freee 勘定科目名(空=独自小計で freee 科目に非対応) */
  freeeAccountName?: string;
  /** 小計ラベルか(freee 科目に対応しない算出行) */
  isSubtotalLabel?: boolean;
  /** C列(別表記候補・部門等の補足) */
  altC?: string;
  /** D列(別表記候補・部門等の補足) */
  altD?: string;
}

export const PL_MAPPING: PlMappingEntry[] = [
  { displayName: '売上', freeeAccountName: '売上高' },
  { displayName: '煙草売上', freeeAccountName: '売上高', altD: '103 煙草販売' },
  { displayName: 'ﾜｲﾝ事業売上', freeeAccountName: '売上高', altD: '101 ワイン販売課' },
  { displayName: '売上値引', freeeAccountName: '社員割引' },
  { displayName: '純売上高', isSubtotalLabel: true },
  { displayName: '期首棚卸高', freeeAccountName: '期首商品棚卸高' },
  { displayName: '包装材期首棚卸高', freeeAccountName: '包装材期首棚卸高' },
  { displayName: '材料仕入', freeeAccountName: '仕入高', altC: '本部仕入' },
  { displayName: 'ワイン仕入', isSubtotalLabel: true },
  { displayName: '包装材仕入', freeeAccountName: '包装材仕入高' },
  { displayName: '期末棚卸高', freeeAccountName: '期末商品棚卸高' },
  { displayName: '包装材期末棚卸高', freeeAccountName: '包装材期末棚卸高' },
  { displayName: '売上原価', isSubtotalLabel: true },
  { displayName: '売上総利益', isSubtotalLabel: true },
  { displayName: '役員報酬', freeeAccountName: '役員報酬' },
  { displayName: '給与手当', freeeAccountName: '給与手当' },
  { displayName: '雑給', freeeAccountName: '雑給' },
  { displayName: '賞与', isSubtotalLabel: true },
  { displayName: '法定福利費', freeeAccountName: '法定福利費' },
  { displayName: '福利厚生費', freeeAccountName: '福利厚生費' },
  { displayName: '通勤費', freeeAccountName: '通勤費' },
  { displayName: '研修費', freeeAccountName: '研修費' },
  { displayName: '募集費', freeeAccountName: '募集費' },
  { displayName: '人件費', isSubtotalLabel: true },
  { displayName: '消耗品費', freeeAccountName: '消耗品費' },
  { displayName: '事務用品費', freeeAccountName: '事務用品費' },
  { displayName: '家賃', freeeAccountName: '地代家賃' },
  { displayName: '賃借料', freeeAccountName: '賃借料' },
  { displayName: '保険料', freeeAccountName: '保険料' },
  { displayName: '修繕費', freeeAccountName: '修繕費' },
  { displayName: '出張費', isSubtotalLabel: true },
  { displayName: '租税公課', freeeAccountName: '租税公課' },
  { displayName: '減価償却費', freeeAccountName: '減価償却費' },
  { displayName: '旅費交通費', freeeAccountName: '旅費交通費' },
  { displayName: '通信費', freeeAccountName: '通信費' },
  { displayName: '水道光熱費', freeeAccountName: '水道光熱費' },
  { displayName: '支払手数料', freeeAccountName: '支払手数料' },
  { displayName: '運 賃', freeeAccountName: '荷造運賃' },
  { displayName: '広告宣伝費', freeeAccountName: '広告宣伝費' },
  { displayName: '交際接待費', freeeAccountName: '交際費' },
  { displayName: '会議費', freeeAccountName: '会議費' },
  { displayName: '燃料費', freeeAccountName: '燃料費' },
  { displayName: '諸会費', freeeAccountName: '諸会費' },
  { displayName: '新聞図書費', freeeAccountName: '新聞図書費' },
  { displayName: '顧問料', freeeAccountName: '顧問料' },
  { displayName: '雑費', freeeAccountName: '雑費', altC: '研究開発費' },
  { displayName: '販売経費', isSubtotalLabel: true, altD: '市場家賃' },
  { displayName: '販売費及び一般管理費', isSubtotalLabel: true, altD: '市場家賃振替' },
  { displayName: '営業利益', isSubtotalLabel: true },
  { displayName: '受取利息', freeeAccountName: '受取利息' },
  { displayName: '受取配当金', freeeAccountName: '受取配当金' },
  { displayName: '雑収入', freeeAccountName: '雑収入' },
  { displayName: '営業外収益', isSubtotalLabel: true },
  { displayName: '支払利息', freeeAccountName: '支払利息' },
  { displayName: '雑損失', freeeAccountName: '雑損失' },
  { displayName: '営業外費用', isSubtotalLabel: true },
  { displayName: '経常利益', isSubtotalLabel: true },
  { displayName: '特別利益', isSubtotalLabel: true },
  { displayName: '特別損失', isSubtotalLabel: true },
  { displayName: '税引前当期純利益', isSubtotalLabel: true },
  { displayName: '当期純利益', isSubtotalLabel: true },
];

// =============================================================================
// raw シートの列スキーマ (freee account_item エクスポート) — Build の値解釈用
//   A=科目名(勘定科目) B=親科目名 C=期首残高 D=借方残高 E=貸方残高 F=期末残高
//   単月 date-range 取得時の freee API 対応:
//     C 期首残高 = opening_balance(前月末累計) / D 借方残高 = debit_amount(当月) /
//     E 貸方残高 = credit_amount(当月) / F 期末残高 = closing_balance(当月末累計)
// =============================================================================

export const RAW_COLUMN_SCHEMA = {
  A: { meaning: '科目名(勘定科目)', freeeField: 'account_item_name' },
  B: { meaning: '親科目名', freeeField: 'parent_account_category_name' },
  C: { meaning: '期首残高(=前月残高)', freeeField: 'opening_balance' },
  D: { meaning: '借方残高(当月借方)', freeeField: 'debit_amount' },
  E: { meaning: '貸方残高(当月貸方)', freeeField: 'credit_amount' },
  F: { meaning: '期末残高(=残高)', freeeField: 'closing_balance' },
} as const;

// =============================================================================
// 全帳票レイアウトの索引
// =============================================================================

export const FINANCIAL_LAYOUTS: Record<ReportLayout['kind'], ReportLayout> = {
  bs: BS_LAYOUT,
  pl: PL_LAYOUT,
  pl_prev: PL_PREV_LAYOUT,
  yoy: YOY_LAYOUT,
  sub_breakdown: SUB_BREAKDOWN_LAYOUT,
};

// =============================================================================
// ROBUSTNESS_POLICY — 新規/廃止勘定科目の自動追従ルール(Build が遵守する契約)
//   (1) 本 layout spec が群定義(detail→群・順序, subtotal→subtotalOf)を保持。
//   (2) Build は freee の全勘定科目を取得し、layout の detail.freeeAccountName と名前一致で値を充当。
//   (3) layout に無い freee 勘定科目(新規)は、freee の account_category(決算書表示名)から
//       CATEGORY_TO_GROUP で対応群を判定し、該当小計群の末尾に detail 行を自動挿入する。
//       判定不能は「その他(未分類)」群へ入れ、meta.unmapped に {accountName, categoryName, amount} を
//       明示記録(黙って落とさない・0埋めしない)。
//   (4) layout にあるが freee に無い科目は 0 表示(科目廃止対応。行は残す)。
//   (5) 群の subtotal は「subtotalOf の明細実値 + その群に自動挿入された新規科目」を合算する。
// =============================================================================

/**
 * freee の account_category_name(決算書表示名) → 本表の所属群(subtotal ラベル)。
 *   ※ category 文字列は freee 実 API レスポンス(account_item_display_type=account_item の
 *      leaf 行 account_category_name)を実測して確定(2026-06-01)。
 *      例: 現金・預金 / 売上債権 / 棚卸資産 / 他流動資産 / 仕入債務 / 他流動負債 ...
 *   ※ 現金・預金 は freee 上は単一カテゴリーだが、会社テンプレは「現金計」「預金計」に分割する。
 *      この分割は科目名の明示列挙(BS_LAYOUT detail 群)で行うため、新規科目の既定受け皿は「預金計」とする
 *      (新口座科目の追加が圧倒的に多いため。現金系の新規は稀)。
 *   ※ PL の人件費は freee 上 category=販売管理費 に内包される。人件費/販売経費の分割は科目名列挙で行うため、
 *      新規 PL 販管費科目の既定受け皿は「販売経費」とする。
 */
export const CATEGORY_TO_GROUP: { category: string; report: 'bs' | 'pl'; group: string }[] = [
  // --- BS 資産 (freee 実カテゴリー名) ---
  { category: '現金・預金', report: 'bs', group: '預金計' },
  { category: '売上債権', report: 'bs', group: '当座試算計' },
  { category: '棚卸資産', report: 'bs', group: '棚卸資産計' },
  { category: '他流動資産', report: 'bs', group: 'その他流動資産計' },
  { category: '有形固定資産', report: 'bs', group: '有形固定資産計' },
  { category: '無形固定資産', report: 'bs', group: '無形固定資産計' },
  { category: '投資その他の資産', report: 'bs', group: '投資その他の資産計' },
  { category: '繰延資産', report: 'bs', group: '繰延資産計' },
  // --- BS 負債 ---
  { category: '仕入債務', report: 'bs', group: '流動負債計' },
  { category: '他流動負債', report: 'bs', group: '流動負債計' },
  { category: '固定負債', report: 'bs', group: '固定負債計' },
  // --- BS 純資産 (freee leaf カテゴリー: 資本金/利益準備金/任意積立金/繰越利益剰余金) ---
  { category: '資本金', report: 'bs', group: '株主資本計' },
  { category: '資本剰余金', report: 'bs', group: '株主資本計' },
  { category: '利益準備金', report: 'bs', group: '株主資本計' },
  { category: '任意積立金', report: 'bs', group: '株主資本計' },
  { category: '繰越利益剰余金', report: 'bs', group: '株主資本計' },
  { category: '評価・換算差額等', report: 'bs', group: '評価・換算差額等計' },
  // --- PL (freee 実カテゴリー名) ---
  { category: '売上高', report: 'pl', group: '純売上高' },
  { category: '期首商品棚卸', report: 'pl', group: '売上原価' },
  { category: '当期商品仕入', report: 'pl', group: '売上原価' },
  { category: '期末商品棚卸', report: 'pl', group: '売上原価' },
  // 売上原価の中間/控除カテゴリー(freee group 階層: 売上原価 > 商品売上原価 > 他勘定振替高(商) 等)。
  //   通常は reports.ts の isUnderClaimed(freee 親子連鎖で売上原価配下と判定)で吸収され二重計上しないが、
  //   freee group 応答の親子連鎖が欠落した場合の保険として明示マップ(resolveGroup フォールバックで売上原価群へ)。
  //   ※ 売上原価 subtotal は freeeCategory='売上原価' の group 集計値採用で newByGroup extra を無視するため二重計上は起きない。
  { category: '商品売上原価', report: 'pl', group: '売上原価' },
  { category: '他勘定振替高(商)', report: 'pl', group: '売上原価' },
  { category: '販売管理費', report: 'pl', group: '販売経費' },
  { category: '営業外収益', report: 'pl', group: '営業外収益' },
  { category: '営業外費用', report: 'pl', group: '営業外費用' },
  { category: '特別利益', report: 'pl', group: '特別利益' },
  { category: '特別損失', report: 'pl', group: '特別損失' },
];

/** 判定不能な新規科目の受け皿群ラベル(BS/PL 共通) */
export const UNMAPPED_GROUP_LABEL = 'その他(未分類)';

export const ROBUSTNESS_POLICY = {
  matchKey: 'freeeAccountName' as const,
  insertNewAtGroupTail: true,
  zeroFillRetiredAccounts: true,
  unmappedGroupLabel: UNMAPPED_GROUP_LABEL,
  categoryToGroup: CATEGORY_TO_GROUP,
} as const;
